import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { RootState } from './store'
import { AuthProvider } from './contexts/AuthContext'
import { MessagesProvider } from './contexts/MessagesContext'
import { ModalProvider, useModal } from './contexts/ModalContext'
import { useAuth } from './contexts/AuthContext'
import BottomNavigation from './components/BottomNavigation'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

// Lazy load all screen components
const HomeScreen = lazy(() => import('./screens/HomeScreen'))
const CampaignsScreen = lazy(() => import('./screens/CampaignsScreen'))
const ProfileScreen = lazy(() => import('./screens/ProfileScreen'))
const AdvertiserProfileScreen = lazy(() => import('./screens/AdvertiserProfileScreen'))
const MessagesScreen = lazy(() => import('./screens/MessagesScreen'))
const LoginScreen = lazy(() => import('./screens/auth/LoginScreen'))
const AuthCallback = lazy(() => import('./screens/auth/AuthCallback'))
const YouTubeCallback = lazy(() => import('./screens/YouTubeCallback'))
const InfluencerSearchScreen = lazy(() => import('./screens/InfluencerSearchScreen'))
const EditProfileScreen = lazy(() => import('./screens/EditProfileScreen'))

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`

const MainContent = styled.main<{ $isAuthenticated: boolean }>`
  flex: 1;
  padding: ${props => props.$isAuthenticated ? '16px 16px 120px' : '16px'};
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 16px;
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007AFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const PageLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 16px;
`

interface NavigationItem {
  label: string;
  path: string;
  icon: string;
}

function AppContent() {
  const { isInitializing, user, token } = useAuth();
  const { isAuthenticated, userType, profileStatus, influencerProfile } = useSelector((state: RootState) => state.profile);
  const { isAnyModalOpen } = useModal();

  // Add debug logging for navigation state
  React.useEffect(() => {
    console.log('Navigation state:', { 
      isAuthenticated, 
      userType, 
      profileStatus, 
      isInitializing,
      hasUser: !!user,
      userId: user?._id,
      hasToken: !!token,
      tokenLength: token?.length,
      pathname: window.location.pathname
    });
  }, [isAuthenticated, userType, profileStatus, isInitializing, user, token]);

  const renderWithSuspense = (Component: React.LazyExoticComponent<any>) => (
    <Suspense fallback={
      <PageLoadingContainer>
        <LoadingSpinner />
        <div>Loading content...</div>
      </PageLoadingContainer>
    }>
      <Component />
    </Suspense>
  );

  // If we're on the login page and not authenticated, show login screen immediately
  if (window.location.pathname === '/login' && !isAuthenticated) {
    return renderWithSuspense(LoginScreen);
  }

  // If we're on the auth callback page, show callback screen immediately
  if (window.location.pathname === '/auth/callback') {
    return renderWithSuspense(AuthCallback);
  }

  // Show loading state only if we're initializing and not on login/callback pages
  if (isInitializing) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <div>Initializing app...</div>
      </LoadingContainer>
    );
  }

  const navigationItems: NavigationItem[] = userType === 'advertiser' ? [
    { label: 'Search', path: '/search', icon: '🔍' },
    { label: 'Campaigns', path: '/campaigns', icon: '📢' },
    { label: 'Messages', path: '/messages', icon: '💬' },
    { label: 'Profile', path: '/profile', icon: '👤' }
  ] : [
    { label: 'Campaigns', path: '/campaigns', icon: '📢' },
    { label: 'Messages', path: '/messages', icon: '💬' },
    { label: 'Profile', path: '/profile', icon: '👤' }
  ];

  return (
    <MessagesProvider>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <AppContainer>
        <MainContent $isAuthenticated={isAuthenticated}>
          <Routes>
            <Route path="/" element={
              isAuthenticated ? (
                userType === 'advertiser' ? (
                  <Navigate to="/search" replace />
                ) : (
                  <Navigate to="/campaigns" replace />
                )
              ) : (
                <HomeScreen />
              )
            } />
            <Route path="/login" element={isAuthenticated ? <Navigate to="/campaigns" /> : <LoginScreen />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/youtube-callback" element={<YouTubeCallback />} />
            <Route 
              path="/campaigns" 
              element={
                isAuthenticated ? (
                  userType === 'influencer' && influencerProfile?.profileStatus === 'draft' ? (
                    <Navigate to="/profile/edit" replace />
                  ) : (
                    renderWithSuspense(CampaignsScreen)
                  )
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route path="/profile" element={
              isAuthenticated ? (
                userType === 'influencer' ? (
                  renderWithSuspense(ProfileScreen)
                ) : (
                  renderWithSuspense(AdvertiserProfileScreen)
                )
              ) : (
                <Navigate to="/login" />
              )
            } />
            <Route path="/profile/edit" element={isAuthenticated ? renderWithSuspense(EditProfileScreen) : <Navigate to="/login" />} />
            <Route path="/search" element={isAuthenticated && userType === 'advertiser' ? renderWithSuspense(InfluencerSearchScreen) : <Navigate to="/login" />} />
            <Route path="/messages" element={isAuthenticated ? renderWithSuspense(MessagesScreen) : <Navigate to="/login" />} />
            <Route path="/advertiser/:id" element={isAuthenticated ? renderWithSuspense(AdvertiserProfileScreen) : <Navigate to="/login" />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </MainContent>
        {isAuthenticated && !window.location.pathname.includes('/privacy-policy') && !window.location.pathname.includes('/terms-of-service') && (
          userType === 'influencer' && influencerProfile?.profileStatus === 'draft' ? null : (
            <BottomNavigation items={navigationItems} hidden={isAnyModalOpen} />
          )
        )}
      </AppContainer>
    </MessagesProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <ModalProvider>
        <AppContent />
      </ModalProvider>
    </AuthProvider>
  );
}

export default App; 